import { useRef, useEffect, useCallback } from 'react';

const DESKTOP_BP = 1024;

const isDesktop = () => window.innerWidth >= DESKTOP_BP;

export const useCountriesScroll = () => {
  const countriesRef = useRef(null);
  const viewRef = useRef(null);
  const gridRef = useRef(null);

  const resetScroll = useCallback(() => {
    if (gridRef.current) gridRef.current.scrollTop = 0;
    if (viewRef.current) viewRef.current.style.height = '';
    const mobileContainer = countriesRef.current?.closest('.content_div');
    if (mobileContainer) mobileContainer.scrollTop = 0;
  }, []);

  useEffect(() => {
    const grid = gridRef.current;
    const panel = viewRef.current;
    if (!grid || !panel) return;

    let initialPanelHeight = 0;
    let rafId = null;

    const init = () => {
      const gridTop = grid.getBoundingClientRect().top;
      if (isDesktop()) {
        const gridHeight = Math.max(window.innerHeight - gridTop + 30, 100);
        grid.style.height = `${gridHeight}px`;
        grid.style.minHeight = '';
        panel.style.height = '';
        initialPanelHeight = panel.offsetHeight;
      } else {
        // Mobile: set minHeight so the grid fills the viewport below the nav,
        // preventing blank space when view-filters or letters collapse.
        const gridMinHeight = Math.max(window.innerHeight - gridTop, 100);
        grid.style.minHeight = `${gridMinHeight}px`;
        grid.style.height = '';
        panel.style.height = '';
        grid.scrollTop = 0;
        const mobileContainer = countriesRef.current?.closest('.content_div');
        if (mobileContainer) mobileContainer.scrollTop = 0;
      }
    };

    // On resize (including orientation change on foldables), read layout after
    // the browser has finished its reflow to get accurate getBoundingClientRect.
    const initAfterLayout = () => {
      requestAnimationFrame(() => requestAnimationFrame(init));
    };

    const handleScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (!isDesktop() || !initialPanelHeight) return;
        const { scrollTop, scrollHeight, clientHeight } = grid;
        const scrollable = scrollHeight - clientHeight;
        if (scrollable <= 0) return;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 5;
        panel.style.height = atBottom
          ? `${initialPanelHeight - 100}px`
          : `${initialPanelHeight}px`;
      });
    };

    init();
    grid.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', initAfterLayout);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      grid.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', initAfterLayout);
    };
  }, []);

  // Exposed so the component can trigger a recompute when internal nav
  // elements change height (e.g. view-filters collapsing on scroll).
  const recompute = useCallback(() => {
    const grid = gridRef.current;
    const panel = viewRef.current;
    if (!grid || !panel) return;
    requestAnimationFrame(() => {
      const gridTop = grid.getBoundingClientRect().top;
      if (isDesktop()) {
        const gridHeight = Math.max(window.innerHeight - gridTop + 50, 100);
        grid.style.height = `${gridHeight}px`;
        grid.style.minHeight = '';
        panel.style.height = '';
      } else {
        const gridMinHeight = Math.max(window.innerHeight - gridTop, 100);
        grid.style.minHeight = `${gridMinHeight}px`;
        grid.style.height = '';
        panel.style.height = '';
      }
    });
  }, []);

  return {
    countriesRef,
    viewRef,
    gridRef,
    resetScroll,
    recompute,
  };
};
